'use client';

import type { ReadonlyURLSearchParams } from 'next/navigation';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { addUrlParameter, removeUrlParameters } from '@/helpers/common';
import { CLIENT_ENVS } from '@/helpers/constants';
import {
  MODAL_QUERY_KEY,
  ModalQueryByName,
  type Modals,
} from '@/helpers/modals';
import { createSelectors } from '../helpers';

export interface OpenModalOptions {
  customOnClose?: CallableFunction;
  replaceUrl?: boolean;
}
export interface CloseModalOptions {
  useCustomOnClose?: boolean;
  replaceUrl?: boolean;
}

export interface ModalsStorageState {
  modal: Modals | null;
  customOnClose: CallableFunction | null;
  open: (modal: Modals, options?: OpenModalOptions) => void;
  close: (options?: CloseModalOptions) => void;
  closeExact: (
    modal: Modals,
    searchParams: ReadonlyURLSearchParams,
    options?: CloseModalOptions
  ) => void;
}

const useModalsStoreBase = create<ModalsStorageState>()(
  devtools(
    (set, getState) => ({
      modal: null,
      customOnClose: null,
      open: (modal, options) => {
        addUrlParameter(
          MODAL_QUERY_KEY,
          ModalQueryByName[modal],
          options?.replaceUrl
        );

        set({ customOnClose: options?.customOnClose || null });
      },
      close: (options) => {
        const { customOnClose } = getState();

        if (options?.useCustomOnClose && customOnClose) {
          customOnClose();
          set({ customOnClose: null });
          return;
        }

        removeUrlParameters([MODAL_QUERY_KEY], options?.replaceUrl);
      },
      closeExact: (modal, searchParams, options) => {
        const { close } = getState();

        if (searchParams.get(MODAL_QUERY_KEY) === ModalQueryByName[modal]) {
          close(options);
        }
      },
    }),
    { name: 'useModalsStore', enabled: CLIENT_ENVS.APP_ENV === 'development' }
  )
);

export const useModalsStore = createSelectors(useModalsStoreBase);
